// assets/eb-region-gate.js
//
// Out-of-market region gate. Governs the #RegionGateModal
// (sections/region-gate-modal.liquid) two ways:
//
//   1. AUTOMATIC — on load, decide whether to show it to a visitor whose
//      detected country is NOT one Element Brooklyn sells to. A dismissible
//      UTILITY message (NN/g: "information critical to continuing"), never a
//      wall. Fail-open at every step: no geo, no EBGeo, an error → no modal.
//        a. window.EBGeo present?            no  → no auto-open
//        b. suppressed (submitted / 14-day)? yes → no auto-open
//        c. EBGeo.get() -> country_code      null→ no auto-open (fail open)
//        d. EBGeo.sellsTo(country_code)?     yes → no auto-open (in-market)
//        e. on the standalone waitlist page (the same form is already
//           inline)?                        yes → no auto-open
//        f. otherwise: fill the country name, open after ~1.2s
//
//   2. MANUAL — a `.js-region-waitlist-trigger` control anywhere on the page
//      (e.g. "Don't see your country?" at the bottom of the country/region
//      modal) opens it directly, bypassing the geo + suppression checks (the
//      visitor explicitly asked for it).
//
// Suppression (localStorage, try/catch-wrapped, { expires_at } convention
// matching assets/scent-society-gate.js). Only AUTOMATIC opens can write a
// dismiss flag; a manual open/close never suppresses the auto-gate:
//   eb_region_gate_dismissed  -> 14 days   (Shopify Geolocation-app default)
//   eb_region_gate_submitted  -> permanent (also set on manual-open submits)
(function () {
  var OPEN_DELAY_MS = 1200;
  var DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;
  var KEY_DISMISSED = 'eb_region_gate_dismissed';
  var KEY_SUBMITTED = 'eb_region_gate_submitted';

  var autoOpened = false; // this page's auto-gate actually opened the modal
  var submitted = false;

  function readFlag(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return false;
      var parsed = JSON.parse(raw);
      if (!parsed) return false;
      if (parsed.expires_at === null) return true; // permanent
      return parsed.expires_at > Date.now();
    } catch (e) {
      return false;
    }
  }

  function writeFlag(key, ttlMs) {
    try {
      var expires_at = ttlMs == null ? null : Date.now() + ttlMs;
      localStorage.setItem(key, JSON.stringify({ expires_at: expires_at }));
    } catch (e) { /* private mode / quota — silent */ }
  }

  // The form lives in a <template> until first use so its country <select>
  // isn't parsed into every page's DOM. Clone it in (once) before showing.
  function hydrate(modal) {
    var tpl = modal.querySelector('template[data-rg-form-template]');
    if (!tpl) return;
    tpl.parentNode.replaceChild(tpl.content.cloneNode(true), tpl);
  }

  function countryName(iso) {
    if (!iso) return '';
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        return new Intl.DisplayNames(['en'], { type: 'region' }).of(String(iso).toUpperCase()) || '';
      }
    } catch (e) {}
    return '';
  }

  function injectCountry(modal, iso) {
    var name = countryName(iso);
    if (!name) return;
    var heading = modal.querySelector('[data-rg-heading]');
    var slot = modal.querySelector('[data-rg-country]');
    if (heading) heading.textContent = "We're not in " + name + ' — yet.';
    if (slot) slot.textContent = name;
  }

  function isSuppressed() {
    return readFlag(KEY_SUBMITTED) || readFlag(KEY_DISMISSED);
  }

  // Only true when we can CONFIDENTLY place the visitor outside a known market.
  // Policy: if in doubt, assume US / in-market and DON'T show. Guards against
  // the failure modes that fired the popup at US visitors:
  //   - empty availableCountries (App Proxy pages render localization empty)
  //   - a non-ISO-alpha2 geo code (e.g. numeric) that Intl still names
  //   - a US visitor
  function outOfMarket(iso) {
    if (!iso) return false;
    iso = String(iso).toUpperCase();
    var avail = window.EBGeo && window.EBGeo.availableCountries;
    if (!avail || avail.length === 0) return false; // unknown context → in-market
    if (!/^[A-Z]{2}$/.test(iso)) return false;       // not a clean ISO alpha-2
    if (iso === 'US') return false;                  // never gate the US
    return !window.EBGeo.sellsTo(iso);
  }

  // Handlers that apply however the modal was opened.
  function wireGlobal(modal) {
    // Permanent suppression once they sign up (fired by the form component) —
    // covers both automatic and manual opens.
    document.addEventListener('region-interest:submitted', function () {
      submitted = true;
      writeFlag(KEY_SUBMITTED, null);
    });

    // "Wrong country?" also carries .js-country-region-trigger, which
    // RegularModal opens on click. Close this modal first so the two don't stack.
    var switchBtn = modal.querySelector('.js-rg-switch-region');
    if (switchBtn) {
      switchBtn.addEventListener('click', function () {
        if (typeof modal.hide === 'function') modal.hide();
      });
    }

    // Manual open from anywhere (e.g. the country/region modal footer).
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.js-region-waitlist-trigger');
      if (!trigger) return;
      e.preventDefault();
      openManually(modal, trigger);
    });
  }

  function openManually(modal, trigger) {
    // Close the country/region RegularModal if it's the opener, so we don't stack.
    var crm = document.querySelector('regular-modal.country-region-modal');
    if (crm && typeof crm.closeModal === 'function') crm.closeModal();

    // Best-effort country name for the headline (cached geo → instant). Only
    // name the country when we're confident it's out-of-market; otherwise the
    // modal keeps its neutral "your country" copy.
    if (window.EBGeo && window.EBGeo.get) {
      window.EBGeo.get().then(function (geo) {
        var iso = geo && geo.country_code;
        if (outOfMarket(iso)) injectCountry(modal, iso);
      }).catch(function () {});
    }

    hydrate(modal);
    if (typeof modal.show === 'function') modal.show(trigger);
  }

  // Dismiss → 14-day suppression. Only armed for AUTOMATIC opens: a manual open
  // (autoOpened stays false) never writes the flag. ModalDialog.hide() dispatches
  // `modalClosed` on document.body after removing the `open` attribute.
  function wireAutoDismiss(modal) {
    document.body.addEventListener('modalClosed', function () {
      if (!autoOpened || submitted) return;
      if (modal.hasAttribute('open')) return; // some other modal closed
      autoOpened = false;
      writeFlag(KEY_DISMISSED, DISMISS_TTL_MS);
    });
  }

  function init() {
    var modal = document.getElementById('RegionGateModal');
    if (!modal) return;

    wireGlobal(modal); // manual trigger + submit/switch handlers always available

    if (!window.EBGeo || !window.EBGeo.get) return; // no auto-gate (fail open)
    if (isSuppressed()) return;
    // Standalone waitlist page already shows the form inline.
    if (document.querySelector('.region-interest-page region-interest-form')) return;

    window.EBGeo.get().then(function (geo) {
      var iso = geo && geo.country_code;
      if (!outOfMarket(iso)) return;          // in-market / unknown → no auto-gate
      if (isSuppressed()) return;             // re-check after async hop

      injectCountry(modal, iso);
      wireAutoDismiss(modal);

      setTimeout(function () {
        if (isSuppressed()) return;
        if (typeof modal.show === 'function') {
          hydrate(modal);
          modal.show(document.body);
          autoOpened = true;
        }
      }, OPEN_DELAY_MS);
    }).catch(function () { /* fail open */ });
  }

  // modal-dialog must be upgraded before we call show(). It's defined in
  // global.js; wait for it so script order doesn't matter.
  function ready() {
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('modal-dialog').then(init).catch(function () {});
    } else {
      init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
})();
